<template>
  <div class="generic-sizes-converter">
    <div class="sizes-converter__filters row">
      <div class="col-xs-12 col-sm-5">
        <p class="sizes-converter__header">{{ $t('size_chart.conversion.convert_from') }}</p>
        <div class="sizes-converter__section row">
          <div class="col-xs-12">
            <p class="sizes-converter__label">{{ $t('generic.gender') }}</p>
            <GenderSelectRadio v-model="genericSizes.genderFrom" :theme="'two-circles'" />
          </div>
          <div class="col-xs-6 col-sm-12">
            <p class="sizes-converter__label">{{ $t('size_chart.conversion.sizing_system') }}</p>
            <DropDownSelect
              v-model="genericSizes.systemFrom"
              :options="sizeSystems"
              :select-phrase="$t('size_chart.conversion.select_sizing_system')"
              :theme-orange="false"
              unique-key="value"
              class="system-from"
            />
          </div>
          <div class="col-xs-6 col-sm-12">
            <p class="sizes-converter__label">{{ $t('size_chart.conversion.size_measurement') }}</p>
            <DropDownSelect
              v-model="genericSizes.sizeFrom"
              :options="sizesOptions"
              :select-phrase="$t('size_chart.conversion.select_size_measurement')"
              :theme-orange="false"
              unique-key="value"
            />
          </div>
        </div>
      </div>

      <div class="col-xs-12 col-sm-2 text-center">
        <div class="arrow">
          <div class="line"></div>
          <div class="point"></div>
        </div>
      </div>

      <div class="col-xs-12 col-sm-5">
        <p class="sizes-converter__header">{{ $t('size_chart.conversion.convert_to') }}</p>
        <div class="sizes-converter__section row">
          <div class="col-xs-12">
            <p class="sizes-converter__label">{{ $t('generic.gender') }}</p>
            <GenderSelectRadio v-model="genericSizes.genderTo" :theme="'two-circles'" />
          </div>
          <div class="col-xs-6 col-sm-12">
            <p class="sizes-converter__label">{{ $t('size_chart.conversion.sizing_system') }}</p>
            <DropDownSelect
              v-model="genericSizes.systemTo"
              :options="sizeSystemsTo"
              :select-phrase="$t('size_chart.conversion.select_sizing_system')"
              :theme-orange="false"
              unique-key="value"
              class="system-from"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="sizes-converter__results" v-if="convertedSizeValue">
      <p>
        {{ genders[genericSizes.genderFrom].label }}’s <strong>{{ genericSizes.systemFrom.label }} {{ genericSizes.sizeFrom.value }}</strong> =
        {{ genders[genericSizes.genderTo].label }}’s
        <strong>{{ genericSizes.systemTo.label }} {{ convertedSizeValue }}</strong>
      </p>
      <a
        :href="localePath({ name: 'search'})+`?sizes-${genericSizes.genderTo}-${genericSizes.systemTo.value}=${genericSizes.systemTo.value}_${convertedSizeValue*10}`"
        class="buy_now_button text-ellipsis"
      >
        {{ $t('size_chart.conversion.see_shoes', { size: genericSizes.systemTo.label+' '+convertedSizeValue }) }}
      </a>
    </div>
  </div>
</template>

<script setup>
import GenderSelectRadio from "~/components/shared/price-comparison/GenderSelectRadio";

import { useGenericSizesStore } from "~/stores/genericSizes";

const { t } = useI18n();

const genericSizes = useGenericSizesStore();

const genders = {
  m: { label: t('generic.men'), value: 'm', id: 'm' },
  w: { label: t('generic.women'), value: 'w', id: 'w' },
};

const sizeSystems = [
  { label: 'CM', value: 'cm' },
  { label: 'IN', value: 'in' },
  { label: 'US', value: 'us' },
  { label: 'UK', value: 'uk' },
  { label: 'EU', value: 'eu' },
  { label: 'Mondo', value: 'mondo' },
];

const sizeSystemsTo = [
  { label: 'US', value: 'us' },
  { label: 'UK', value: 'uk' },
  { label: 'EU', value: 'eu' },
];

const sizesOptions = computed(() => {
  return genericSizes.systemFrom
    ? [
      ...new Set(
        genericSizes.sizesStructure.map(sizesMap => {
          const key = genericSizes.buildSizeMapIndex(
            genericSizes.systemFrom.value.toLowerCase(),
            genericSizes.genderFrom ? genericSizes.genderFrom : '',
          );
          return sizesMap[key];
        }),
      ),
    ].map(s => {
      return { label: s, value: s };
    })
    : [];
})

const convertedSizeValue = computed(() => {
  if (!genericSizes.convertedSize) {
    return null;
  }
  const sizeMapIndexTo = genericSizes.sizesMap.indexOf(
    genericSizes.buildSizeMapIndex(genericSizes.systemTo.value, genericSizes.genderTo),
  );
  return sizeMapIndexTo > -1 ? genericSizes.convertedSize[sizeMapIndexTo] : null;
})
</script>

<style lang="scss" scoped>
.generic-sizes-converter {
  border: 1px solid #cccccc;
  border-radius: 4px;
  margin-top: 30px;
  margin-bottom: 30px;
  padding: 15px;

  .sizes-converter__section {
    & > div {
      margin-bottom: 15px;

      @media (max-width: 480px) {
        width: 100%;
      }
    }
  }

  .sizes-converter__header {
    font-weight: 400;
    font-size: 21px;
  }

  .sizes-converter__label {
    margin-bottom: 4px;
    font-weight: 400;
  }

  .arrow {
    display: inline-block;
    margin-top: 105px;
    width: 48px;
    height: 48px;
    position: relative;
    border: 1px solid #ccc;
    border-radius: 48px;
    padding: 14px 10px;

    @media (max-width: $break_sm_under) {
      margin-top: 0;
      margin-bottom: 10px;
    }
    .line {
      margin-top: 6px;
      width: 14px;
      background: $grey-dark;
      height: 6px;
      float: left;
      border: 1px solid $grey-dark;
      border-radius: 1px;
      @media (max-width: $break_sm_under) {
        width: 6px;
        height: 14px;
        margin-top: -2px;
        margin-left: 10px;
      }
    }

    .point {
      width: 0;
      height: 0;
      border-top: 9px solid transparent;
      border-bottom: 9px solid transparent;
      border-left: 12px solid $grey-dark;
      float: right;
      @media (max-width: $break_sm_under) {
        width: 0;
        height: 0;
        border-top: 12px solid $grey-dark;
        border-right: 9px solid transparent;
        border-left: 9px solid transparent;
        float: right;
        position: absolute;
        bottom: 0px;
        left: 14px;
      }
    }
  }

  .sizes-converter__filters {
    margin-bottom: 16px;
  }

  .sizes-converter__results {
    text-align: center;
    border: 1px solid $border-color;
    border-radius: 4px;
    background-color: $bg_transparent_orange;
    position: relative;
    margin: -16px -16px;
    padding: 30px 0;

    .buy_now_button {
      padding: 0 16px;
    }
  }
}
</style>
